import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { statsApi, vouchersApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatusBadge } from '../components/shared/StatusBadge';
import { formatCurrency, formatDate, getStatusConfig } from '../lib/utils';
import { Link } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import StorageStats from '../components/admin/StorageStats';

const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <Card className="hover:shadow-lg transition-shadow duration-200">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-semibold font-mono mt-1">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, vouchersData] = await Promise.all([
          statsApi.get(),
          vouchersApi.getAll(),
        ]);
        setStats(statsData);
        setRecentVouchers(vouchersData.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-20 bg-gray-100 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const statusCounts = stats?.statusCounts || {};
  const roleBasedStats = () => {
    if (user?.role === 'employee') {
      return [
        { title: 'Total Vouchers', value: stats?.totalVouchers || 0, icon: FileText, color: 'bg-blue-100 text-blue-600' },
        { title: 'Drafts', value: statusCounts.DRAFT || 0, icon: Clock, color: 'bg-gray-100 text-gray-600' },
        { title: 'Pending Approval', value: statusCounts.SUBMITTED || 0, icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
        { title: 'Approved', value: (statusCounts.MANAGER_APPROVED || 0) + (statusCounts.ACCOUNTS_APPROVED || 0) + (statusCounts.PAID || 0), icon: CheckCircle, color: 'bg-green-100 text-green-600' },
      ];
    } else if (user?.role === 'manager') {
      return [
        { title: 'Total Vouchers', value: stats?.totalVouchers || 0, icon: FileText, color: 'bg-blue-100 text-blue-600' },
        { title: 'Pending Approvals', value: statusCounts.SUBMITTED || 0, icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
        { title: 'Approved by Me', value: (statusCounts.MANAGER_APPROVED || 0) + (statusCounts.ACCOUNTS_APPROVED || 0) + (statusCounts.PAID || 0), icon: CheckCircle, color: 'bg-green-100 text-green-600' },
        { title: 'Rejected', value: statusCounts.MANAGER_REJECTED || 0, icon: XCircle, color: 'bg-red-100 text-red-600' },
      ];
    } else if (user?.role === 'accounts') {
      return [
        { title: 'Awaiting Approval', value: statusCounts.MANAGER_APPROVED || 0, icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
        { title: 'Approved', value: statusCounts.ACCOUNTS_APPROVED || 0, icon: CheckCircle, color: 'bg-green-100 text-green-600' },
        { title: 'Paid', value: statusCounts.PAID || 0, icon: DollarSign, color: 'bg-emerald-100 text-emerald-600' },
        { title: 'Rejected', value: statusCounts.ACCOUNTS_REJECTED || 0, icon: XCircle, color: 'bg-red-100 text-red-600' },
      ];
    } else {
      return [
        { title: 'Total Vouchers', value: stats?.totalVouchers || 0, icon: FileText, color: 'bg-blue-100 text-blue-600' },
        { title: 'Pending', value: (statusCounts.DRAFT || 0) + (statusCounts.SUBMITTED || 0), icon: Clock, color: 'bg-yellow-100 text-yellow-600' },
        { title: 'Approved', value: (statusCounts.ACCOUNTS_APPROVED || 0) + (statusCounts.PAID || 0), icon: CheckCircle, color: 'bg-green-100 text-green-600' },
        { title: 'Rejected', value: (statusCounts.MANAGER_REJECTED || 0) + (statusCounts.ACCOUNTS_REJECTED || 0), icon: XCircle, color: 'bg-red-100 text-red-600' },
      ];
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's an overview of your voucher activities
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {roleBasedStats().map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Admin Storage Stats */}
      {user?.role === 'admin' && (
        <StorageStats />
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6">
        {/* Recent Vouchers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Recent Vouchers</CardTitle>
            <Link
              to={
                user?.role === 'employee' ? '/vouchers' 
                : user?.role === 'manager' ? '/approvals' 
                : user?.role === 'accounts' ? '/accounts/all'
                : user?.role === 'internal_audit' ? '/internal-audit/queue'
                : user?.role === 'finance_head' ? '/finance-head/all'
                : '/admin/vouchers'
              }
              className="text-sm text-[#6a5da4] hover:underline"
            >
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {recentVouchers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No vouchers found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentVouchers.map((voucher) => (
                  <Link
                    key={voucher.id}
                    to={
                      user?.role === 'employee'
                        ? `/vouchers/${voucher.id}`
                        : user?.role === 'manager'
                        ? `/approvals/${voucher.id}`
                        : user?.role === 'accounts'
                        ? `/accounts/vouchers/${voucher.id}`
                        : user?.role === 'internal_audit'
                        ? `/internal-audit/vouchers/${voucher.id}`
                        : user?.role === 'finance_head'
                        ? `/finance-head/vouchers/${voucher.id}`
                        : `/vouchers/${voucher.id}`
                    }
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                    data-testid={`voucher-row-${voucher.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {voucher.voucherNo || 'Draft'}
                        </span>
                        <StatusBadge status={voucher.status} size="sm" />
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {voucher.beneficiaryName}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-mono text-sm font-medium">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(voucher.date)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
